import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import { Button } from "../../../components/button";
import { BulkLineItem, BulkServiceType } from "../../../../../model/bulk-purchase/bulk-purchase-customer";
import { BULK_AMOUNT_PRESETS } from "../../bulk-purchase.types";
import { formatCurrency } from "../../../../../utils";
import styles from "./meter-form.module.scss";

interface MeterFormProps {
	/** When set, the form is in edit mode and pre-filled. */
	initialItem?: BulkLineItem | null;
	submitLabel: string;
	onSubmit: (item: BulkLineItem) => void;
	onCancel?: () => void;
	currency?: string;
}

export const MeterForm: React.FC<MeterFormProps> = ({ initialItem, submitLabel, onSubmit, onCancel, currency = "NGN" }) => {
	const { t } = useTranslation();
	const [serviceType, setServiceType] = useState<BulkServiceType | null>(initialItem?.serviceType ?? null);
	const [meterNumber, setMeterNumber] = useState<string>(initialItem?.meterNumber ?? "");
	const [amount, setAmount] = useState<string>(initialItem ? String(initialItem.amount) : "");

	const handleSubmit = () => {
		if (!serviceType) {
			toast.error(t("buyElectricity.bulk.selectMeterTypeRequired"));
			return;
		}
		if (!meterNumber.trim()) {
			toast.error(t("buyElectricity.bulk.meterNumberRequired"));
			return;
		}
		const numericAmount = Number.parseFloat(amount);
		if (!numericAmount || numericAmount < 1) {
			toast.error(t("buyElectricity.bulk.amountGreaterThanZero"));
			return;
		}
		onSubmit({
			id: initialItem?.id ?? uuidv4(),
			serviceType,
			meterNumber: meterNumber.trim(),
			amount: numericAmount,
		});
	};

	return (
		<div className={styles.form}>
			<div className={styles.section}>
				<p className={styles.sectionLabel}>{t("buyElectricity.bulk.selectMeterType")}</p>
				<div className={styles.typeRow}>
					{(["Prepaid", "Postpaid"] as BulkServiceType[]).map(type => (
						<button
							type="button"
							key={type}
							className={`${styles.typePill} ${serviceType === type ? styles.typePillActive : ""}`}
							onClick={() => setServiceType(type)}
						>
							<span className={styles.radioOuter}>{serviceType === type && <span className={styles.radioInner} />}</span>
							{type === "Prepaid" ? t("common.labels.prepaid") : t("common.labels.postpaid")}
						</button>
					))}
				</div>
			</div>

			<div className={styles.section}>
				<p className={styles.sectionLabel}>
					{serviceType === "Postpaid" ? t("buyElectricity.bulk.accountNumber") : t("common.labels.meterNumber")}
				</p>
				<input
					className={styles.input}
					inputMode="numeric"
					value={meterNumber}
					onChange={e => setMeterNumber(e.target.value.replace(/[^0-9]/g, ""))}
					placeholder={t("buyElectricity.bulk.enterMeterNumberExample")}
				/>
			</div>

			<div className={styles.section}>
				<p className={styles.sectionLabel}>{t("common.labels.amount")}</p>
				<div className={styles.chips}>
					{BULK_AMOUNT_PRESETS.map(preset => (
						<button
							type="button"
							key={preset}
							className={`${styles.chip} ${Number.parseFloat(amount) === preset ? styles.chipActive : ""}`}
							onClick={() => setAmount(String(preset))}
						>
							{formatCurrency(preset, { currency, decimals: 0 })}
						</button>
					))}
				</div>
				<p className={styles.customLabel}>{t("buyElectricity.bulk.customAmount")}</p>
				<NumericFormat
					className={styles.input}
					value={amount}
					onValueChange={values => setAmount(values.value)}
					thousandSeparator=","
					decimalSeparator="."
					decimalScale={2}
					allowNegative={false}
					placeholder="0.00"
					inputMode="decimal"
				/>
			</div>

			<div className={styles.actions}>
				{onCancel && (
					<Button variant="outline" fullWidth onClick={onCancel}>
						{t("common.actions.cancel")}
					</Button>
				)}
				<Button variant="primary" fullWidth className={styles.primaryAction} onClick={handleSubmit}>
					{submitLabel}
				</Button>
			</div>
		</div>
	);
};

export default MeterForm;
